import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Spinner } from "@/components/ui/spinner";
import { ScrollToTop } from "@/components/common/ScrollToTop";
import { CookieConsent } from "@/components/common/CookieConsent";
import { trackPageView } from "@/lib/analytics";

// Layouts
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { AdminLayout } from "@/layouts/AdminLayout";
import { PublicLayout } from "@/layouts/PublicLayout";

// Public Pages
import { LandingPage } from "@/pages/public/LandingPage";
import { ResetPasswordPage } from "@/pages/public/ResetPasswordPage";
import { VerifyEmailPage } from "@/pages/public/VerifyEmailPage";
import { TermsPage } from "@/pages/public/TermsPage";
import { PrivacyPage } from "@/pages/public/PrivacyPage";
import { CookiesPage } from "@/pages/public/CookiesPage";
import { ArsredovisningPage } from "@/pages/public/ArsredovisningPage";
import { K2Page } from "@/pages/public/K2Page";
import { K3Page } from "@/pages/public/K3Page";
import { VilandeAktiebolagPage } from "@/pages/public/VilandeAktiebolagPage";
import { GuiderPage } from "@/pages/public/GuiderPage";
import { ForstaArsredovisningenPage } from "@/pages/public/guider/ForstaArsredovisningenPage";
import { SieFilPage } from "@/pages/public/guider/SieFilPage";
import { K2VsK3Page } from "@/pages/public/guider/K2VsK3Page";
import { NyaRegler2026Page } from "@/pages/public/guider/NyaRegler2026Page";
import { NoterPage } from "@/pages/public/guider/NoterPage";
import { ForvaltningsberattelsePage } from "@/pages/public/guider/ForvaltningsberattelsePage";
import { AktiebokPage } from "@/pages/public/AktiebokPage";
import { PriserPage } from "@/pages/public/PriserPage";
import { OmOssPage } from "@/pages/public/OmOssPage";
import { FAQPage } from "@/pages/public/FAQPage";
import { KontaktPage } from "@/pages/public/KontaktPage";
import { Dashboard } from "@/pages/Dashboard";
import { Companies } from "@/pages/Companies";
import { CompanyDetail } from "@/pages/CompanyDetail";
import { NewReport } from "@/pages/NewReport";
import { Settings } from "@/pages/Settings";
import { Billing } from "@/pages/Billing";
import { Notifications } from "@/pages/Notifications";
import { WizardPage } from "@/pages/wizard";
import { PublicWizardPage } from "@/pages/wizard/PublicWizardPage";
import { AcceptInvitation } from "@/pages/AcceptInvitation";
import { ShareBookPage } from "@/pages/share-book";
import { IntegrationCallback } from "@/pages/integrations";

// Admin Pages
import {
  AdminDashboard,
  AdminUsers,
  AdminCompanies,
  AdminReports,
  AdminShareBooks,
  AdminProtocols,
  AdminAuditLogs,
  AdminSettings,
  AdminRoles,
  AdminPricing,
  AdminSubscriptions,
  AdminStatistics,
  AdminEmailCron,
} from "@/pages/admin";

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Public route - redirect to dashboard if already logged in (kept for future use)
function _PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
void _PublicRoute; // Preserve for future use

export default function App() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  // Track page views on route changes (only for public pages)
  useEffect(() => {
    // Only track public pages (not dashboard/admin)
    const isPublicPage = !location.pathname.startsWith("/dashboard") &&
                         !location.pathname.startsWith("/admin");
    if (isPublicPage) {
      trackPageView(location.pathname);
    }
  }, [location.pathname]);

  // Determine if we should show cookie consent (only on public pages)
  const showCookieConsent = !isAuthenticated &&
    !location.pathname.startsWith("/dashboard") &&
    !location.pathname.startsWith("/admin");

  return (
    <>
      <ScrollToTop />
      {showCookieConsent && <CookieConsent />}
      <Routes>
      {/* Public routes - Landing page accessible to all users (logged in or not) */}
      <Route path="/" element={<LandingPage />} />

      {/* Auth pages (public, no redirect if logged in) */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/invitations/accept" element={<AcceptInvitation />} />

      {/* Integration OAuth callback (public, handles redirect from providers) */}
      <Route path="/integrations/callback/:provider" element={<IntegrationCallback />} />
      <Route path="/integrations/callback" element={<IntegrationCallback />} />

      {/* Public wizard (no auth required) */}
      <Route path="/wizard" element={<PublicWizardPage />} />

      {/* Public content pages (SEO pages, visible to all) */}
      <Route element={<PublicLayout />}>
        {/* Legal pages */}
        <Route path="/villkor" element={<TermsPage />} />
        <Route path="/integritetspolicy" element={<PrivacyPage />} />
        <Route path="/cookies" element={<CookiesPage />} />

        {/* Årsredovisning cluster */}
        <Route path="/arsredovisning" element={<ArsredovisningPage />} />
        <Route path="/arsredovisning/k2" element={<K2Page />} />
        <Route path="/arsredovisning/k3" element={<K3Page />} />
        <Route path="/arsredovisning/vilande-aktiebolag" element={<VilandeAktiebolagPage />} />

        {/* Guider */}
        <Route path="/arsredovisning/guider" element={<GuiderPage />} />
        <Route
          path="/arsredovisning/guider/forsta-arsredovisningen"
          element={<ForstaArsredovisningenPage />}
        />
        <Route path="/arsredovisning/guider/sie-fil" element={<SieFilPage />} />
        <Route path="/arsredovisning/guider/k2-vs-k3" element={<K2VsK3Page />} />
        <Route path="/arsredovisning/guider/nya-regler-2026" element={<NyaRegler2026Page />} />
        <Route path="/arsredovisning/guider/noter" element={<NoterPage />} />
        <Route
          path="/arsredovisning/guider/forvaltningsberattelse"
          element={<ForvaltningsberattelsePage />}
        />

        {/* Other product pages */}
        <Route path="/aktiebok" element={<AktiebokPage />} />
        <Route path="/priser" element={<PriserPage />} />

        {/* Company pages */}
        <Route path="/om-oss" element={<OmOssPage />} />
        <Route path="/vanliga-fragor" element={<FAQPage />} />
        <Route path="/kontakt" element={<KontaktPage />} />
      </Route>

      {/* Protected routes with dashboard layout */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="companies" element={<Companies />} />
        <Route path="companies/:companyId" element={<CompanyDetail />} />
        <Route path="companies/:companyId/share-book" element={<ShareBookPage />} />
        <Route path="companies/:companyId/new-report" element={<NewReport />} />
        <Route path="settings" element={<Settings />} />
        <Route path="billing" element={<Billing />} />
        <Route path="notifications" element={<Notifications />} />
      </Route>

      {/* Wizard routes (outside dashboard layout) */}
      <Route
        path="/dashboard/wizard/:reportId"
        element={
          <ProtectedRoute>
            <WizardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/wizard/:reportId/step/:step"
        element={
          <ProtectedRoute>
            <WizardPage />
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="companies" element={<AdminCompanies />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="share-books" element={<AdminShareBooks />} />
        <Route path="protocols" element={<AdminProtocols />} />
        <Route path="subscriptions" element={<AdminSubscriptions />} />
        <Route path="audit-logs" element={<AdminAuditLogs />} />
        <Route path="roles" element={<AdminRoles />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="pricing" element={<AdminPricing />} />
        <Route path="statistics" element={<AdminStatistics />} />
        <Route path="email-cron" element={<AdminEmailCron />} />
      </Route>

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
